<template>
  <div id="app" :class="{ 'invalid-location': !isValidLocation }">
    <div class="main-container">
      <!-- Step 1: Hiển thị thông tin nhân viên -->
      <div v-if="step === 1" class="employee-info">
        <div class="content-wrapper">
          <img class="avatar" :src="userAvatar" alt="Avatar" />
          <div class="info">
            <h1>{{ userInfo?.displayName || 'Loading...' }}</h1>
            <p class="department">{{ userInfo?.jobTitle || 'Nhân viên' }}</p>
            <p class="email">{{ userInfo?.userPrincipalName || 'Loading...' }}</p>
            
            <!-- Kết hợp thời gian và vị trí -->
            <div class="status-info">
              <div class="time-location-display">
                <p class="current-time">{{ formattedCurrentTime }}</p>
                <p v-if="currentLocation && isValidLocation" class="current-location">
                  {{ getCurrentLocationName }}
                </p>
              </div>
            </div>

            <!-- Thông báo trạng thái -->
            <div class="status-container">
              <div v-if="checkingLocation" class="greeting checking">
                <span class="loader"></span>
                Đang kiểm tra vị trí làm việc...
              </div>
              <div v-else-if="checkingWorkHours" class="greeting checking">
                <span class="loader"></span>
                Đang kiểm tra giờ làm việc...
              </div>
              <div v-else-if="!hasLocationPermission" class="greeting error">
                Vui lòng cấp quyền truy cập vị trí để tiếp tục chấm công
              </div>
              <div v-else-if="!isValidLocation" class="greeting error">
                Bạn đang ở ngoài vị trí chấm công!
              </div>
              <div v-else-if="!isValidWorkingHours" class="greeting error">
                Giờ làm việc không hợp lệ (08:00 - 17:30)
              </div>
              <div v-else class="greeting success">
                Xin chào {{ userInfo?.displayName }}! Hãy chấm công để khởi đầu ngày làm việc!
              </div>
            </div>

            <!-- Nút chấm công -->
            <button v-if="canCheckIn" 
                    @click="startWork" 
                    class="checkin-btn">
              Chấm công
            </button>

            <!-- Danh sách địa điểm làm việc -->
            <div class="valid-locations">
              <div class="locations-grid">
                <div v-for="(location, index) in validLocations" 
                     :key="index" 
                     class="location-item"
                     :class="{ 'current': isCurrentLocation(location) }">
                  {{ location.name }}
                </div>
              </div>
            </div>

            <button @click="logout" class="logout-btn">
              <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
  <path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h6a2 2 0 012 2v1" />
</svg>
              Đăng xuất
            </button>
          </div>
        </div>
      </div>

      <!-- Step 2: Giao diện đếm giờ làm việc -->
      <div v-if="step === 2" class="working-time">
        <div class="working-content">
          <div class="working-header">
            <h2>Thời gian làm việc</h2>
            <p class="checkin-time">Bắt đầu từ: {{ formatStartTime }}</p>
          </div>
          
          <div class="time-circle">
            <span>{{ formattedWorkingTime }}</span>
          </div>

          <button @click="checkout" class="checkout-btn">
            Checkout
          </button>
        </div>
      </div>

      <!-- Step 3: Màn hình tóm tắt kết quả chấm công -->
      <div v-if="step === 3" class="checkout-summary">
        <div class="summary-content">
          <h2>Tóm tắt kết quả chấm công</h2>
          
          <div class="employee-brief">
            <img class="avatar" :src="userAvatar" alt="Avatar" />
            <div class="info">
              <h1>{{ userInfo?.displayName }}</h1>
              <p class="email">{{ userInfo?.userPrincipalName }}</p>
            </div>
          </div>

          <div class="time-summary">
            <div class="summary-item">
              <p class="label">Thời gian làm việc:</p>
              <p class="value">{{ formattedWorkingTime }}</p>
            </div>
            <div class="summary-item">
              <p class="label">Ngày công hôm nay:</p>
              <p class="value">{{ workDayValue }}</p>
            </div>
          </div>

          <p class="thank-you">Cảm ơn bạn vì một ngày làm việc hiệu quả!</p>
          <button @click="nextS" class="checkout-btn">Tiếp tục</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import api from '@/api/axios';
import { useAuthStore } from '@/stores/authStore';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';

export default {
  setup() {
    const step = ref(1);
    const currentTime = ref(new Date());
    const startTime = ref(null); // Khởi tạo là null
    const timer = ref(null);
    const userInfo = ref(null);
    const loading = ref(true);
    const authStore = useAuthStore();
    const router = useRouter();
    const currentLocation = ref(null);
    const isValidLocation = ref(false);
    const hasLocationPermission = ref(false);
    const checkingLocation = ref(true);
    const checkingWorkHours = ref(true);
    const isValidWorkingHours = ref(false);
    const locationError = ref(false);

    const validLocations = [
      { name: 'HQ Office', lat: 19.77457475814397, lng: 105.79362481317715, radius: 500 },
      { name: 'HKOIL Điền Lư', lat: 10.7742, lng: 106.7017, radius: 200 },
      { name: 'HKOIL Mường Lát', lat: 10.7805, lng: 106.6991, radius: 200 },
      { name: 'HKOIL Quảng Vinh', lat: 10.7831, lng: 106.6945, radius: 200 },
      { name: 'HKOIL Sơn Tuyền', lat: 10.7866, lng: 106.6977, radius: 200 },
      { name: 'HKOIL Pù Nhi', lat: 10.7897, lng: 106.6925, radius: 200 },
      { name: 'HKOIL Quảng Thành', lat: 19.77498612761552, lng: 105.79314439551709, radius: 500 },
      { name: 'Hà Nội', lat: 21.001410384161954, lng: 105.73884084025967, radius: 2000 },
    ];

    // Computed properties
    const lunchStart = ref(new Date().setHours(12, 0, 0, 0)); // 12:00
    const lunchEnd = ref(new Date().setHours(13, 30, 0, 0));  // 13:30

    const formattedWorkingTime = computed(() => {
      if (!startTime.value) return '00:00:00';
      
      // Prevent negative time
      const now = Math.max(currentTime.value.getTime(), startTime.value.getTime());
      const start = startTime.value.getTime();
      let totalWorkTime = 0;
      
      const lunchStartTime = new Date().setHours(12, 0, 0, 0);
      const lunchEndTime = new Date().setHours(13, 30, 0, 0);
      
      if (start < lunchEndTime) {
        if (now <= lunchStartTime) {
          totalWorkTime = now - start;
        } else if (now >= lunchEndTime) {
          totalWorkTime = (now - lunchEndTime) + (lunchStartTime - start);
        } else {
          totalWorkTime = lunchStartTime - start;
        }
      } else {
        totalWorkTime = now - start;
      }

      // Ensure non-negative values 
      const seconds = Math.max(0, Math.floor(totalWorkTime / 1000));
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;

      // Always return positive padded numbers
      return `${String(Math.abs(hours)).padStart(2, '0')}:${String(Math.abs(minutes)).padStart(2, '0')}:${String(Math.abs(remainingSeconds)).padStart(2, '0')}`;
    });

    const formattedCurrentTime = computed(() => {
      return currentTime.value.toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/[,]/g, '');
    });

    const checkWorkingHours = computed(() => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const currentTimeMinutes = hours * 60 + minutes;
      
      const startTimeMinutes = 8 * 60; // 00:00
      const endTimeMinutes = 23 * 60 + 59; // 23:59
      
      return currentTimeMinutes >= startTimeMinutes && currentTimeMinutes <= endTimeMinutes;
    });

    const userAvatar = computed(() => {
      return 'https://ik.imagekit.io/lcpyg6uwo/img/512hklogo.png';
    });

    const canCheckIn = computed(() => {
      return isValidLocation.value && 
             isValidWorkingHours.value && 
             !checkingLocation.value && 
             hasLocationPermission.value;
    });

    const getCurrentLocationName = computed(() => {
      if (!currentLocation.value) return '';
      const found = validLocations.find(location => 
        calculateDistance(
          currentLocation.value.lat,
          currentLocation.value.lng,
          location.lat,
          location.lng
        ) <= location.radius
      );
      return found ? found.name : 'Không xác định';
    });

    const formatStartTime = computed(() => {
      return new Date(startTime.value).toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    });

    const workDayValue = computed(() => {
      if (!startTime.value) return 0;
      
      const totalWorkHours = getTotalWorkHours();
      
      if (totalWorkHours >= 8) return 1;
      if (totalWorkHours >= 4) return 0.5;
      return 0;
    });

    const getTotalWorkHours = () => {
      if (!startTime.value) return 0;
      
      const now = currentTime.value.getTime();
      const start = startTime.value.getTime();
      let totalWorkTime = 0;
      
      const lunchStartTime = new Date().setHours(12, 0, 0, 0);
      const lunchEndTime = new Date().setHours(13, 30, 0, 0);
      
      if (start < lunchEndTime) {
        if (now <= lunchStartTime) {
          totalWorkTime = now - start;
        } else if (now >= lunchEndTime) {
          totalWorkTime = (now - lunchEndTime) + (lunchStartTime - start);
        } else {
          totalWorkTime = lunchStartTime - start;
        }
      } else {
        totalWorkTime = now - start;
      }

      return totalWorkTime / (1000 * 60 * 60); // Convert to hours
    };

    // Methods
    const updateTime = () => {
      currentTime.value = new Date();
      if (!checkingLocation.value) {
        isValidWorkingHours.value = checkWorkingHours.value;
      }
    };

    const startWork = () => {
      startTime.value = new Date(); // Lưu thời điểm bắt đầu thực tế
      step.value = 2;
      // Cập nhật thời gian mỗi giây
      timer.value = setInterval(updateTime, 1000);
      
      // Lưu thông tin chấm công
      const checkInData = {
        startTime: startTime.value.getTime(),
        step: step.value
      };
      localStorage.setItem('checkInData', JSON.stringify(checkInData));
    };

    const checkout = () => {
      const totalWorkHours = getTotalWorkHours();
      
      if (totalWorkHours < 8) {
        Swal.fire({
          title: 'Xác nhận checkout',
          html: `<div style="font-size: 1.1em">
                  <p>Thời gian làm việc của bạn là <b style="color: #e74c3c">${Math.floor(totalWorkHours * 100) / 100} giờ</b></p>
                  <p>Chưa đủ 1 ngày công (8 giờ)</p>
                  <p>Bạn có chắc chắn muốn checkout không?</p>
                </div>`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Đồng ý',
          cancelButtonText: 'Hủy'
        }).then((result) => {
          if (result.isConfirmed) {
            clearInterval(timer.value);
            step.value = 3;
            localStorage.removeItem('checkInData');
          }
        });
      } else {
        clearInterval(timer.value);
        step.value = 3;
        localStorage.removeItem('checkInData');
      }
    };

    const nextS = () => {
      router.push('/att-pe');
    };

    const getUserInfo = async () => {
      try {
        const response = await api.get('https://graph.microsoft.com/v1.0/me', {
          headers: { Authorization: `Bearer ${authStore.accessToken}` },
        });
        userInfo.value = response.data;
      } catch (error) {
        console.error('Error fetching user info:', error);
        router.push('/');
      }
    };

    const logout = async () => {
      try {
        authStore.clearTokens();
        authStore.isAuthenticated = false;
        router.push('/');
      } catch (error) {
        console.error('Logout error:', error);
        router.push('/dang-nhap');
      }
    };

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
      const R = 6371e3;
      const φ1 = lat1 * Math.PI/180;
      const φ2 = lat2 * Math.PI/180;
      const Δφ = (lat2-lat1) * Math.PI/180;
      const Δλ = (lon2-lon1) * Math.PI/180;

      const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
                Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ/2) * Math.sin(Δλ/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

      return R * c;
    };

    const isCurrentLocation = (location) => {
      if (!currentLocation.value) return false;
      const distance = calculateDistance(
        currentLocation.value.lat,
        currentLocation.value.lng,
        location.lat,
        location.lng
      );
      return distance <= location.radius;
    };

    const getCurrentPosition = () => {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported'));
          return;
        }

        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        });
      });
    };

    const validateLocation = async () => {
      if (!currentLocation.value) {
        isValidLocation.value = false;
        return false;
      }

      isValidLocation.value = validLocations.some(location => {
        const distance = calculateDistance(
          currentLocation.value.lat,
          currentLocation.value.lng,
          location.lat,
          location.lng
        );
        return distance <= location.radius;
      });

      checkingWorkHours.value = true;
      setTimeout(() => {
        isValidWorkingHours.value = checkWorkingHours.value;
        checkingWorkHours.value = false;
      }, 1000);
    };

    const requestLocation = async () => {
      try {
        checkingLocation.value = true;
        locationError.value = false;

        if (!navigator.geolocation) {
          throw new Error('Geolocation is not supported');
        }

        const position = await getCurrentPosition();
        currentLocation.value = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        hasLocationPermission.value = true;
        await validateLocation();
      } catch (error) {
        console.error('Location error:', error);
        locationError.value = true;
        hasLocationPermission.value = false;
        isValidLocation.value = false;
      } finally {
        checkingLocation.value = false;
      }
    };

    const restoreCheckInState = () => {
      const savedData = localStorage.getItem('checkInData');
      if (savedData) {
        const data = JSON.parse(savedData);
        startTime.value = new Date(data.startTime);
        step.value = data.step;
        if (step.value === 2) {
          timer.value = setInterval(updateTime, 1000);
        }
      }
    };

    onMounted(() => {
      getUserInfo();
      requestLocation();
      restoreCheckInState();
      // Không cần interval ở đây vì sẽ bắt đầu khi click startWork
    });

    return {
      step,
      userInfo,
      loading,
      currentTime,
      timer,
      startTime,
      formattedCurrentTime,
      formattedWorkingTime,
      validLocations,
      currentLocation,
      isValidLocation,
      hasLocationPermission,
      checkingLocation,
      checkingWorkHours,
      isValidWorkingHours,
      canCheckIn,
      getCurrentLocationName,
      userAvatar,
      startWork,
      checkout,
      nextS,
      logout,
      isCurrentLocation,
      locationError,
      formatStartTime,
      lunchStart,
      lunchEnd,
      workDayValue,
      getTotalWorkHours, // Thêm vào return để có thể sử dụng trong template
    };
  }
};
</script>

<style scoped>
#app {
  font-family: 'Arial', sans-serif;
  background: linear-gradient(45deg, #ff6b6b, #f3c47b, #f884f6);
  background-size: 300% 300%;
  animation: gradientAnimation 6s ease infinite;
  min-height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;
}

.main-container {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.employee-info {
  width: 100%;
  max-width: none;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 30px;
  backdrop-filter: blur(10px);
}

.content-wrapper {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 30px;
  align-items: start;
}

.info {
  text-align: left;
}

.locations-grid {
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 15px;
  width: 100%;
}

.time-location-display {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 15px 25px;
  background: rgba(255, 255, 255, 0.15);
  margin: 15px auto;
  max-width: 600px;
  border-radius: 10px;
}

.employee-info, .working-time, .checkout-summary {
  text-align: center;
  margin-top: 20px;
  width: 100%;
}

.employee-info {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  backdrop-filter: blur(10px);
}

.avatar {
  border-radius: 50%;
  width: 120px;
  height: 120px;
  object-fit: cover;
  margin-bottom: 20px;
  border: 3px solid #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}

.info h1 {
  font-size: 28px;
  font-weight: bold;
}

.checkin-time {
  font-size: 24px;
  font-weight: bold;
  color: #2ecc71; /* Nổi bật thời gian check-in với màu xanh lá */
}

.greeting {
  font-size: 20px;
  color: #fff;
  padding: 15px 25px;
  border-radius: 12px;
  background: linear-gradient(45deg, #ff6b6b, #f39c12);
  display: inline-block;
  margin: 15px 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  animation: fadeInOut 0.3s ease-in-out;
  position: relative;
  text-align: center;
  min-width: 300px;
  max-width: 80%;
  /* Xóa transform và left để không bị lệch trái phải */
  margin-left: auto;
  margin-right: auto;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.working-time {
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  padding: 20px;
}

.working-content {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 20px;
  width: 100%;
  max-width: 500px;
  text-align: center;
}

.working-header {
  margin-bottom: 30px;
}

.working-header h2 {
  font-size: 28px;
  font-weight: bold;
  color: #fff;
  margin: 0;
}

.day-status {
  font-size: 18px;
  color: #fff;
  margin: 10px 0 0;
  opacity: 0.9;
}

.time-circle {
  width: 250px;
  height: 250px;
  background: rgb(46 204 113 / 56%);
  border: 3px solid #2ecc71;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 48px;
  font-weight: bold;
  margin: 30px auto;
  border-radius: 50%;
  box-shadow: 0 0 30px rgba(46, 204, 113, 0.3);
}

.checkout-btn {
  padding: 12px 30px;
  font-size: 18px;
  font-weight: bold;
  background: linear-gradient(45deg, #ff6b6b, #f39c12, #f19f9f); /* Gradient sinh động */
  color: white;
  border: none;
  border-radius: 30px; /* Bo góc mềm mại */
  cursor: pointer;
  margin-top: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3); /* Đổ bóng nhẹ tạo chiều sâu */
  transition: all 0.3s ease-in-out; /* Thêm hiệu ứng chuyển động */
}

.checkout-btn:hover {
  transform: translateY(-5px); /* Nâng nút lên khi hover */
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3); /* Tăng bóng khi hover */
  background: linear-gradient(45deg, #f39c12, rgba(209, 132, 132, 0.15), #ff6b6b); /* Đảo ngược gradient khi hover */
}

.checkout-btn:active {
  transform: translateY(2px); /* Nhấn nút xuống khi click */
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2); /* Bóng nhẹ khi click */
}

.thank-you {
  font-size: 18px;
  font-weight: bold;
  color: #8b6be1; /* Màu chữ vàng nổi bật */
  background: linear-gradient(45deg, #F5C495 #f39c12); /* Gradient xanh lá và vàng */
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.2); /* Đổ bóng nhẹ để làm nổi bật */
  animation: gradientEffect 3s ease-in-out infinite; /* Thêm hiệu ứng gradient động */
}

@keyframes gradientEffect {
  0% {
    background: linear-gradient(45deg, #edb50c92, #f39c12);
  }
  50% {
    background: linear-gradient(45deg, #f4d90c, #f1c40f); /* Thay đổi màu sắc của gradient */
  }
  100% {
    background: linear-gradient(45deg, #ebbd0799, #f39c12);
  }
}

.day-summary {
  font-size: 16px;
  color: #fff;
  margin-top: 20px;
}

/* New styles */
.location-status {
  display: none;
}

.time-display {
  background: rgba(255, 255, 255, 0.15);
  padding: 15px;
  border-radius: 12px;
  margin: 15px 0;
  backdrop-filter: blur(5px);
}

.current-time {
  font-size: 28px;
  font-weight: bold;
  color: #fff;
  margin: 0;
}

.location-status-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
  width: 100%;
}

.status-message {
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 10px;
}

.status-message.checking {
  background: rgba(255, 255, 255, 0.2);
}

.status-message.error {
  background: rgba(255, 87, 87, 0.2);
}

.status-message.success {
  background: rgba(46, 204, 113, 0.2);
}

.valid-locations {
  margin-top: 20px;
}

.locations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
  margin-top: 15px;
}

.location-item {
  padding: 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  color: #fff;
}

.location-item.current {
  background: rgba(46, 204, 113, 0.3);
  border: 2px solid #2ecc71;
  transform: scale(1.05);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .locations-grid {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  }

  .location-item {
    font-size: 12px;
    padding: 6px;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .avatar {
    width: 180px;
    height: 180px;
  }

  .info h1 {
    font-size: 24px;
  }

  .greeting {
    font-size: 18px;
    min-width: auto;
    width: 90%;
  }

  .time-circle {
    width: 150px;
    height: 150px;
    font-size: 30px;
  }
}

@media (max-width: 480px) {
  .avatar {
    width: 120px;
    height: 120px;
  }

  .info h1 {
    font-size: 20px;
  }

  .greeting {
    font-size: 16px;
  }

  .time-circle {
    width: 120px;
    height: 120px;
    font-size: 24px;
  }
}

/* Support for dark mode */
@media (prefers-color-scheme: dark) {
  .checking-location {
    background: rgba(40, 40, 40, 0.9);
    color: #fff;
  }
}

.greeting.checking {
  background: linear-gradient(45deg, #3498db, #2980b9);
}

.greeting.error {
  background: linear-gradient(45deg, #e74c3c, #c0392b);
}

.greeting.success {
  background: linear-gradient(45deg, #2ecc71, #27ae60);
  max-width: 100%;
}

.loader {
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-right: 10px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.location-info {
  margin-top: 15px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 16px;
}

.checkin-btn {
  display: block;
  margin: 15px auto 0;
  padding: 10px 25px;
  font-size: 16px;
  font-weight: bold;
  background: linear-gradient(45deg, #2ecc71, #27ae60);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.checkin-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.checkin-btn:active {
  transform: translateY(1px);
}

/* New styles */
.content-wrapper {
  max-height: 140vh;
  padding: 20px;
  margin: 0 auto;
}

.status-info {
  margin: 15px 0;
}

.time-location-display {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  background: rgba(255, 255, 255, 0.1);
  padding: 10px 20px;
  border-radius: 10px;
  backdrop-filter: blur(5px);
}

.current-time {
  font-size: 24px;
  font-weight: bold;
  color: #fff;
  margin: 0;
}

.current-location {
  font-size: 18px;
  color: #fff;
  margin: 0;
  opacity: 0.9;
}

.greeting {
  width: fit-content;
  margin: 15px auto;
  padding: 12px 20px;
}

.checkin-btn {
  display: block;
  margin: 25px auto;
  padding: 12px 40px;
  font-size: 18px;
  font-weight: bold;
  background: linear-gradient(45deg, #2ecc71, #27ae60);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.valid-locations {
  margin-top: 25px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .content-wrapper {
    padding: 10px;
  }

  .time-location-display {
    flex-direction: column;
    gap: 10px;
  }
}

@media (max-width: 1200px) {
  .content-wrapper {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .info {
    text-align: center;
  }

  .locations-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
}

@media (max-width: 768px) {
  #app {
    padding: 10px;
  }

  .employee-info {
    padding: 15px;
  }

  .locations-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}

.info h1, .info p, .department, .email {
  color: #fff;
}

.status-container {
  width: 100%;
  max-width: 600px;
  margin: 20px auto;
  min-height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  /* Đảm bảo nội dung luôn ở giữa */
  text-align: center;
}

.checkout-summary {
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.summary-content {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 600px;
  text-align: center;
}

.summary-content h2 {
  color: #fff;
  font-size: 28px;
  margin-bottom: 30px;
}

.employee-brief {
  margin: 20px 0;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
}

.employee-brief .avatar {
  width: 100px;
  height: 100px;
  margin-bottom: 15px;
}

.employee-brief h1 {
  color: #fff;
  font-size: 24px;
  margin: 0;
}

.employee-brief .email {
  color: #fff;
  opacity: 0.9;
  margin: 5px 0 0;
}

.time-summary {
  margin: 30px 0;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  margin: 10px 0;
}

.summary-item .label {
  color: #fff;
  opacity: 0.9;
  margin: 0;
}

.summary-item .value {
  color: #fff;
  font-weight: bold;
  font-size: 20px;
  margin: 0;
}

.thank-you {
  color: #fff;
  font-size: 18px;
  margin: 30px 0;
  padding: 15px;
  background: rgba(46, 204, 113, 0.2);
  border-radius: 10px;
  border: 1px solid rgba(46, 204, 113, 0.3);
}

@media (max-width: 768px) {
  .summary-content {
    padding: 20px;
  }

  .employee-brief .avatar {
    width: 80px;
    height: 80px;
  }

  .summary-item {
    flex-direction: column;
    gap: 5px;
  }

  .thank-you {
    font-size: 16px;
  }
}
/* Thêm styles mới cho logout button */
.logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 30px auto 0;
  padding: 12px 30px;
  background: linear-gradient(45deg, #e74c3c, #c0392b);
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
}

.logout-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(231, 76, 60, 0.3);
}

.logout-icon {
  width: 20px;
  height: 20px;
  margin-bottom: -4px;
}
.checkin-time {
  color: #fff;
  font-size: 18px;
  margin: 10px 0;
  opacity: 0.8;
}
</style>
